import type { ReactNode } from 'react'
import { motion, useReducedMotion } from 'motion/react'
import { GetInTouchTrigger } from '@/components/get-in-touch-trigger'
import { MorningEyebrowPill } from '@/components/morning-eyebrow-pill'
import { contactSubmitButtonClassName } from '@/components/contact-form-styles'
import { usePrefersReducedMotion } from '@/hooks/use-prefers-reduced-motion'
import { cn } from '@/lib/utils'

const heroPrimaryClass = contactSubmitButtonClassName
const headingFontClass = 'font-heading-outfit'
const accentClass = 'font-hero-accent font-normal tracking-normal italic'

const LINE_1 = 'Web development'
const LINE_2 = 'worth waking up for'

const revealEase = [0.16, 1, 0.3, 1] as const

type HeroLandingContentProps = {
  className?: string
  /** When true, plays the hero copy load-in (after the sunrise finishes). */
  reveal?: boolean
}

const headingClassName = cn(
  'mx-auto max-w-[40ch] text-4xl font-medium tracking-tighter text-balance text-white sm:text-5xl md:text-6xl lg:text-7xl',
  headingFontClass,
)

function HeroTitleClipReveal({
  reduced,
  play,
}: {
  reduced: boolean
  play: boolean
}) {
  const show = reduced || play

  return (
    <h1 className={headingClassName} aria-label={`${LINE_1} ${LINE_2}`}>
      <span className="block overflow-hidden pb-[0.08em]">
        <motion.span
          className="inline-block"
          initial={reduced ? false : { y: '110%' }}
          animate={show ? { y: '0%' } : { y: '110%' }}
          transition={{
            duration: 0.85,
            ease: revealEase,
            delay: reduced || !play ? 0 : 0.08,
          }}
          aria-hidden
        >
          {LINE_1}
        </motion.span>
      </span>
      <span className="block overflow-hidden pb-[0.08em]">
        <motion.span
          className={cn('inline-block', accentClass)}
          initial={reduced ? false : { y: '110%' }}
          animate={show ? { y: '0%' } : { y: '110%' }}
          transition={{
            duration: 0.85,
            ease: revealEase,
            delay: reduced || !play ? 0 : 0.22,
          }}
          aria-hidden
        >
          {LINE_2}
        </motion.span>
      </span>
    </h1>
  )
}

function HeroFadeUp({
  reduced,
  play,
  delay,
  className,
  children,
}: {
  reduced: boolean
  play: boolean
  delay: number
  className?: string
  children: ReactNode
}) {
  const show = reduced || play

  return (
    <motion.div
      className={className}
      initial={reduced ? false : { opacity: 0, y: 14 }}
      animate={show ? { opacity: 1, y: 0 } : { opacity: 0, y: 14 }}
      transition={{
        duration: 0.7,
        ease: revealEase,
        delay: reduced || !play ? 0 : delay,
      }}
    >
      {children}
    </motion.div>
  )
}

export function HeroLandingContent({
  className,
  reveal = false,
}: HeroLandingContentProps) {
  const framerReducedMotion = useReducedMotion()
  const a11yReducedMotion = usePrefersReducedMotion()
  const reduced = Boolean(framerReducedMotion) || a11yReducedMotion

  return (
    <div className={cn('text-center', className)}>
      <HeroFadeUp reduced={reduced} play={reveal} delay={0} className="mb-6">
        <MorningEyebrowPill>Established 2007</MorningEyebrowPill>
      </HeroFadeUp>
      <div className="flex flex-col items-center gap-6">
        <HeroTitleClipReveal reduced={reduced} play={reveal} />
        <HeroFadeUp reduced={reduced} play={reveal} delay={0.36}>
          <p className="mx-auto max-w-[44ch] text-lg/relaxed text-pretty text-white/80 sm:text-lg/relaxed lg:text-xl/relaxed">
            We design, build, and maintain web solutions for partners all over
            the world. Are you ready to say G&apos;Morning?
          </p>
        </HeroFadeUp>
        <HeroFadeUp
          reduced={reduced}
          play={reveal}
          delay={0.48}
          className="flex flex-wrap items-center justify-center gap-4 md:[@media(max-height:729px)]:invisible"
        >
          <GetInTouchTrigger className={heroPrimaryClass} />
          <a
            href="#services"
            className="inline-flex items-center gap-1.5 text-base font-semibold text-white/85 transition-colors hover:text-white sm:text-sm dark:hover:text-white"
          >
            Our services
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1}
              stroke="currentColor"
              className="size-5 sm:size-4"
              aria-hidden
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m8.25 4.5 7.5 7.5-7.5 7.5"
              />
            </svg>
          </a>
        </HeroFadeUp>
      </div>
    </div>
  )
}
